// ChatHistory.js
import { useState, useMemo } from "react";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../../../components/Loader";
import ChatMessages from "./ChatMessages";
import MessageInput from "./MessageInput";
import TemplateDrawer from "./TemplateDrawer";
import AddTemplateModal from "./TemplateDrawer/AddTemplateModal";
import EditTemplateModal from "./TemplateDrawer/EditTemplateModal";
import { useChatMessages } from "./hooks/useChatMessages";
import { useTemplates } from "./hooks/useTemplates";
import { get, post } from "../../../services/ApiService";
import UnreadQueueNav from "./UnreadQueueNav";

const ChatHistory = ({
  canViewMsg,
  canSendMsg,
  messages,
  isChatLoading,
  isNoMessage,
  NoMessageText,
  textareaRef,
  message,
  setMessage,
  attachments,
  setAttachments,
  faqs,
  setFaqs,
  handleSendMessage,
  isSending,
  order,
  questions,
  ticket,
  handleCloseTicket,
  hasUnreadMessage,
  setHasUnreadMessage,
  hasMoreMessages,
  loadOlderMessages,
  amazonMessagingInfo,
  setOrder,
}) => {
  const { chatMessages, setChatMessages, chatContainerRef, isLoadingOlder } = useChatMessages(messages, loadOlderMessages, hasMoreMessages);
  const { templates, setTemplates, isDragging, onDragStart, onDragEnd } = useTemplates(faqs);
  
  const [loadingButtons, setLoadingButtons] = useState({});
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [faqVisible, setFaqVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [awaitingReplyLoading, setAwaitingReplyLoading] = useState(false);

  const showAwaitingReply = canSendMsg && Boolean(setOrder) && order?.status === "PENDING";

  const handleAwaitingReply = async () => {
    if (!order?._id || !setOrder || awaitingReplyLoading) return;
    const next = !order.awaitingReply;
    setAwaitingReplyLoading(true);
    try {
      const res = await post(`api/orders/${order._id}/awaiting-reply`, {
        awaitingReply: next,
      });
      if (res?.success) {
        setOrder((prev) => ({
          ...prev,
          awaitingReply: res.awaitingReply,
          awaitingReplyAt: res.awaitingReplyAt,
        }));
        toast.success(next ? "Marked as awaiting reply" : "Awaiting reply cleared");
      } else {
        toast.error(res?.error || "Failed to update awaiting reply");
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update awaiting reply");
    } finally {
      setAwaitingReplyLoading(false);
    }
  };

  const formatName = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };
  
  const templateFields = useMemo(() => ({

    orderId: order?.orderId || "",
    name: formatName(order?.buyerInfo?.fullname) || "",
    username: order?.buyerInfo?.username || "",
    address: `${order?.shippingInfo?.addressLine1},${order?.shippingInfo?.addressLine2},${order?.shippingInfo?.city},${order?.shippingInfo?.zip}` || "",
    postCode: order?.shippingInfo?.zip || "",
    shippingDate: order?.shippingDate
    ? isValidDate(new Date(order.shippingDate)) 
      ? new Date(order.shippingDate).toLocaleDateString()
      : "Invalid date"
    : "No shipping date",
    trackingNumber: order?.trackingNumber || "No tracking number",
    carrier:
      order?.carrier === "RM"
        ? "Royal Mail"
        : order?.carrier === "AMZ"
        ? "Amazon"
        : "",
    questions:
      questions && questions.length > 0
        ? questions.map((q, i) => `${q}`).join("\n")
        : "No questions available",
    productName:
      order?.products && order.products.length > 0
        ? order.products.map((p) => `${p.title}`).join(", ")
        : "No products available",
    productLink:order?.products && order.products.length > 0
        ? order.products.map((p) => `${p.itemUrl}`).join(", ")
        : "No product link available",
  }), [order, questions]);
  

  const replaceTemplates = (text) => {
    let result = text;
    Object.entries(templateFields).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    });
    return result;
  };

  const markReadMessage = async (id) => {
    try {
      setLoadingButtons((prev) => ({ ...prev, [id]: true }));
      const res = await get(`api/messages/mark-as-read/${id}`);
      if (res.success) {
        setChatMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg._id === id ? { ...msg, read: true } : msg
          )
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingButtons((prev) => ({ ...prev, [id]: false }));
    }
  };
  
  function isValidDate(d) {
    return d instanceof Date && !isNaN(d);
  }

  const handleCreateFaq = async (values) => {
    try {
      const res = await post("api/external-log/faqs/add", values);
      setFaqs([...faqs, res.faq]);
      toast.success("FAQ added successfully!");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add FAQ");
      return false;
    }
  };

  const handleUpdateFaq = async (id, values) => {
    try {
      const res = await post(`api/external-log/faqs/update/${id}`, values);
      if(res.success){
        setFaqs(faqs.map(faq => 
          faq._id === id ? { ...faq, ...values } : faq
        ));
        toast.success("Template updated successfully!");
        return true;
      }
      toast.error(res.error);
      return false;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update FAQ");
      return false;
    }
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="chat-panel">
        <div className="chat-panel__header">
          <div className="chat-panel__identity">
            <span className="chat-panel__avatar" aria-hidden="true">
              <i className="fas fa-comment-dots" />
            </span>
            <div className="chat-panel__meta">
              <h6 className="chat-panel__name">{order?.buyerInfo?.fullname}</h6>
              {ticket?.ticketNo && (
                <span className="chat-panel__ticket">Ticket {ticket.ticketNo}</span>
              )}
            </div>
          </div>
          {showAwaitingReply && (
            <button
              type="button"
              className="chat-panel__awaiting"
              onClick={handleAwaitingReply}
              disabled={awaitingReplyLoading}
            >
              {awaitingReplyLoading
                ? "Please wait..."
                : order.awaitingReply
                  ? "Unmark awaiting reply"
                  : "Mark awaiting reply"}
            </button>
          )}
        </div>

        <div className="chat-panel__body">
          {isChatLoading && <Loader />}

          {canViewMsg && (
            <ChatMessages
              messages={chatMessages}
              isNoMessage={isNoMessage}
              NoMessageText={NoMessageText}
              loadingButtons={loadingButtons}
              markReadMessage={markReadMessage}
              containerRef={chatContainerRef}
              isLoadingOlder={isLoadingOlder}
              hasMoreMessages={hasMoreMessages}
            />
          )}
        </div>

        <MessageInput
          order={order}
          message={message}
          attachments={attachments}
          setMessage={setMessage}
          setAttachments={setAttachments}
          textareaRef={textareaRef}
          handleSendMessage={handleSendMessage}
          isSending={isSending}
          onTemplateOpen={() => setIsTemplateOpen(true)}
          replaceTemplates={replaceTemplates}
          ticket={ticket}
          handleCloseTicket={handleCloseTicket}
          hasUnreadMessage={hasUnreadMessage}
          setHasUnreadMessage={setHasUnreadMessage}
          amazonMessagingInfo={amazonMessagingInfo}
          canSendMsg={canSendMsg}
        />
      </div>

      <UnreadQueueNav orderId={order?._id} />

      <TemplateDrawer
        isOpen={isTemplateOpen}
        onClose={() => setIsTemplateOpen(false)}
        templates={templates}
        editMode={editMode}
        setEditMode={setEditMode}
        onDragEnd={onDragEnd}
        isDragging={isDragging}
        onDragStart={onDragStart}
          onTemplateSelect={(content) => {
            setMessage(replaceTemplates(content));
            setIsTemplateOpen(false);
          }}
        onEditTemplate={setEditingFaq}
        onAddTemplate={() => setFaqVisible(true)}
      />
      
      <AddTemplateModal
        visible={faqVisible}
        onCancel={() => setFaqVisible(false)}
        onSave={handleCreateFaq}
        isSending={isSending}
      />
      
      <EditTemplateModal
        template={editingFaq}
        onCancel={() => setEditingFaq(null)}
        onSave={handleUpdateFaq}
        isSending={isSending}
      />
    </>
  );
};

export default ChatHistory;
